import { FunctionComponent, useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AdminSideMenu from "../components/AdminSideMenu";
import { useRooms } from "../contexts/RoomContext";
import styles from "./MyRoomScreen.module.css";
import AdminTopMenu from "../components/AdminTopMenu/AdminTopMenu";
import {
  startRemoteRecording,
  stopRemoteRecording,
  getRemoteRecordingStatusBulk,
  startRemoteRecordingBulk,
  stopRemoteRecordingBulk,
  RemoteRecordingStatus,
} from "../services/roomService";

// AG Grid imports
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, SelectionChangedEvent, GetRowIdParams } from 'ag-grid-community';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

// Define Room interface locally for AG Grid typing
interface RoomForGrid {
  id: number;
  roomId?: string; // Custom Room ID
  name: string;
  roomType?: string;
  enableTranscription?: boolean;
  // status: string; // Status is derived or static for now
}

const MyRoomScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const { rooms, loading, error, fetchRooms, removeRoom, setCurrentRoom, totalRooms, currentPage, totalPages } = useRooms();
  const [quickFilterText, setQuickFilterText] = useState('');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [deletingRoomId, setDeletingRoomId] = useState<number | null>(null);
  // Rooms for which a remote-start window is currently active.
  const [activeRecordingIds, setActiveRecordingIds] = useState<Set<number>>(new Set());
  // Rooms with an in-flight start/stop API call so we can disable the button.
  const [busyRecordingIds, setBusyRecordingIds] = useState<Set<number>>(new Set());
  // Rooms selected via checkboxes for bulk operations.
  const [selectedRoomIds, setSelectedRoomIds] = useState<number[]>([]);
  // In-flight bulk start / stop so we can disable the toolbar buttons.
  const [bulkBusy, setBulkBusy] = useState<'start' | 'stop' | null>(null);
  // Full remote-recording status per room (liveness of the Electron desktop app). Used
  // to surface a warning on the row when a Start command has been issued but the desktop
  // app is offline or hasn't acknowledged that it's actually recording yet.
  const [remoteStatusById, setRemoteStatusById] = useState<Record<number, RemoteRecordingStatus>>({});
  // Rooms where the Electron speaker itself reports isRecording=true (could be started
  // from the admin panel OR directly by the person at the desktop app). Separate from
  // `activeRecordingIds` which only reflects admin-issued remote-start windows.
  const [clientRecordingIds, setClientRecordingIds] = useState<Set<number>>(new Set());
  // Rooms for which admin just issued a Start so we still show a "Waiting…" state even
  // before the first liveness poll comes back.
  const [pendingStartSinceMs, setPendingStartSinceMs] = useState<Record<number, number>>({});
  // Forces the row warning to re-evaluate its elapsed time every second.
  const [, setNowTick] = useState<number>(Date.now());

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchRooms({ page, limit: pageSize });
    }
  }, [fetchRooms, page, pageSize]);

  // Shared helper that applies a batch of status results to every liveness state slice.
  // Kept as a top-level function expression so both the initial load and the polling
  // intervals can share identical update logic.
  const applyStatusResults = useCallback((
    results: ReadonlyArray<readonly [number, RemoteRecordingStatus] | null>,
  ) => {
    setRemoteStatusById((prev) => {
      let changed = false;
      const next = { ...prev };
      results.forEach((r) => {
        if (!r) return;
        const [id, status] = r;
        if (next[id] !== status) { next[id] = status; changed = true; }
      });
      return changed ? next : prev;
    });

    setActiveRecordingIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      results.forEach((r) => {
        if (!r) return;
        const [id, status] = r;
        if (status.active && !prev.has(id)) { next.add(id); changed = true; }
        else if (!status.active && prev.has(id)) { next.delete(id); changed = true; }
      });
      return changed ? next : prev;
    });

    setClientRecordingIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      results.forEach((r) => {
        if (!r) return;
        const [id, status] = r;
        // Only trust `clientRecording` when the client is also online -- otherwise the
        // value could be the last reported state of a dead speaker.
        const recording = status.clientOnline === true && status.clientRecording === true;
        if (recording && !prev.has(id)) { next.add(id); changed = true; }
        else if (!recording && prev.has(id)) { next.delete(id); changed = true; }
      });
      return changed ? next : prev;
    });

    setPendingStartSinceMs((prev) => {
      let changed = false;
      const next = { ...prev };
      results.forEach((r) => {
        if (!r) return;
        const [id, status] = r;
        if (next[id] && (status.clientRecording === true || !status.active)) {
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  // Keep rooms in a ref so the slow all-rooms poll below doesn't need `rooms` as a dep
  // (which would recreate its interval every time rooms change).
  const roomsRef = useRef<typeof rooms>([]);
  useEffect(() => { roomsRef.current = rooms; }, [rooms]);

  // Whenever the visible rooms list changes, refresh the remote-recording status in bulk
  // so the Start/Stop button + liveness warnings reflect the server state after a reload.
  useEffect(() => {
    let cancelled = false;
    if (!rooms || rooms.length === 0) {
      setActiveRecordingIds(new Set());
      setClientRecordingIds(new Set());
      setRemoteStatusById({});
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const ids = rooms.map((r) => r.id);
        const resp = await getRemoteRecordingStatusBulk(ids);
        if (cancelled) return;
        const entries = resp.results.map((r) =>
          r.success && r.data
            ? ([Number(r.identifier), r.data] as const)
            : null
        );
        applyStatusResults(entries);
      } catch {
        // ignore; buttons will default to "Start Recording"
      }
    })();
    return () => { cancelled = true; };
  }, [rooms, applyStatusResults]);

  // Keep a ref with the IDs we currently want to FAST-poll so the polling effect below
  // can read the latest set without taking these state values as deps. Taking them as
  // deps would recreate the interval on every state update we make from inside the tick,
  // causing an immediate effect re-run -> fresh tick() call -> setState -> re-run ...
  // i.e. an infinite loop that hammered the server with thousands of requests.
  const pollIdsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    const combined = new Set<number>();
    activeRecordingIds.forEach((id) => combined.add(id));
    clientRecordingIds.forEach((id) => combined.add(id));
    Object.keys(pendingStartSinceMs).forEach((k) => combined.add(Number(k)));
    pollIdsRef.current = combined;
  }, [activeRecordingIds, clientRecordingIds, pendingStartSinceMs]);

  // FAST poll (every 4s) for rooms that are currently interesting: admin issued a Start,
  // Electron is recording, or we're still waiting for the first heartbeat after Start.
  // Uses the bulk endpoint so this is always 1 HTTP request regardless of N.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const ids = Array.from(pollIdsRef.current);
      if (ids.length === 0) return;
      try {
        const resp = await getRemoteRecordingStatusBulk(ids);
        if (cancelled) return;
        const results = resp.results.map((r) =>
          r.success && r.data ? ([Number(r.identifier), r.data] as const) : null
        );
        applyStatusResults(results);
      } catch {
        // best-effort; the next tick will try again
      }
    };
    tick();
    const handle = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [applyStatusResults]);

  // SLOW poll (every 12s) across every visible room so the admin still sees "Recording"
  // when a speaker starts recording directly from the desktop app. This is the only way
  // to discover recordings that were never triggered from this admin panel. Also a single
  // bulk request -- cheap at 100+ rooms.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const list = roomsRef.current;
      if (!list || list.length === 0) return;
      try {
        const ids = list.map((r) => r.id);
        const resp = await getRemoteRecordingStatusBulk(ids);
        if (cancelled) return;
        const results = resp.results.map((r) =>
          r.success && r.data ? ([Number(r.identifier), r.data] as const) : null
        );
        applyStatusResults(results);
      } catch {
        // best-effort; the next tick will try again
      }
    };
    // Don't run immediately -- the initial-load effect already fetched everything.
    const handle = setInterval(tick, 12000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [applyStatusResults]);

  // 1-second wall-clock tick so "Waiting Xs" elapsed counters re-render. Guarded by a
  // ref-based check so this effect itself only runs once on mount.
  const hasPendingRef = useRef<boolean>(false);
  useEffect(() => {
    hasPendingRef.current = Object.keys(pendingStartSinceMs).length > 0;
  }, [pendingStartSinceMs]);
  useEffect(() => {
    const h = setInterval(() => {
      if (hasPendingRef.current) setNowTick(Date.now());
    }, 1000);
    return () => clearInterval(h);
  }, []);

  const handleStartRecording = useCallback(async (roomId: number) => {
    setBusyRecordingIds(prev => { const next = new Set(prev); next.add(roomId); return next; });
    try {
      await startRemoteRecording(roomId);
      setActiveRecordingIds(prev => { const next = new Set(prev); next.add(roomId); return next; });
      // Track when we issued Start so the row can show "Waiting for desktop app…" until
      // Electron polls in and reports recording=true (or until timeout elapses).
      setPendingStartSinceMs(prev => ({ ...prev, [roomId]: Date.now() }));
    } catch (e: any) {
      console.error('Failed to start remote recording:', e);
      alert(`Failed to start recording: ${e?.response?.data?.error || e?.message || 'Unknown error'}`);
    } finally {
      setBusyRecordingIds(prev => { const next = new Set(prev); next.delete(roomId); return next; });
    }
  }, []);

  const handleStopRecording = useCallback(async (roomId: number) => {
    setBusyRecordingIds(prev => { const next = new Set(prev); next.add(roomId); return next; });
    try {
      await stopRemoteRecording(roomId);
      setActiveRecordingIds(prev => { const next = new Set(prev); next.delete(roomId); return next; });
      setPendingStartSinceMs(prev => { if (!prev[roomId]) return prev; const n = { ...prev }; delete n[roomId]; return n; });
    } catch (e: any) {
      console.error('Failed to stop remote recording:', e);
      alert(`Failed to stop recording: ${e?.response?.data?.error || e?.message || 'Unknown error'}`);
    } finally {
      setBusyRecordingIds(prev => { const next = new Set(prev); next.delete(roomId); return next; });
    }
  }, []);

  const onSelectionChanged = useCallback((event: SelectionChangedEvent<RoomForGrid>) => {
    const selected = event.api.getSelectedRows();
    const ids = selected.map(r => r.id).filter((id): id is number => typeof id === 'number');
    setSelectedRoomIds(ids);
  }, []);

  const handleBulkStart = useCallback(async () => {
    if (selectedRoomIds.length === 0 || bulkBusy) return;
    setBulkBusy('start');
    setBusyRecordingIds(prev => { const next = new Set(prev); selectedRoomIds.forEach(id => next.add(id)); return next; });
    try {
      const resp = await startRemoteRecordingBulk(selectedRoomIds);
      const successIds = new Set<number>(
        resp.results.filter(r => r.success).map(r => Number(r.id))
      );
      setActiveRecordingIds(prev => {
        const next = new Set(prev);
        successIds.forEach(id => next.add(id));
        return next;
      });
      setPendingStartSinceMs(prev => {
        const next = { ...prev };
        const now = Date.now();
        successIds.forEach(id => { next[id] = now; });
        return next;
      });
      if (resp.successes < resp.total) {
        const failed = resp.results.filter(r => !r.success);
        console.warn('Bulk start had failures:', failed);
        alert(`Started ${resp.successes}/${resp.total} rooms. ${failed.length} failed.`);
      }
    } catch (e: any) {
      console.error('Bulk start failed:', e);
      alert(`Bulk start failed: ${e?.response?.data?.error || e?.message || 'Unknown error'}`);
    } finally {
      setBusyRecordingIds(prev => { const next = new Set(prev); selectedRoomIds.forEach(id => next.delete(id)); return next; });
      setBulkBusy(null);
    }
  }, [selectedRoomIds, bulkBusy]);

  const handleBulkStop = useCallback(async () => {
    if (selectedRoomIds.length === 0 || bulkBusy) return;
    setBulkBusy('stop');
    setBusyRecordingIds(prev => { const next = new Set(prev); selectedRoomIds.forEach(id => next.add(id)); return next; });
    try {
      const resp = await stopRemoteRecordingBulk(selectedRoomIds);
      const successIds = new Set<number>(
        resp.results.filter(r => r.success).map(r => Number(r.id))
      );
      setActiveRecordingIds(prev => {
        const next = new Set(prev);
        successIds.forEach(id => next.delete(id));
        return next;
      });
      setPendingStartSinceMs(prev => {
        const next = { ...prev };
        successIds.forEach(id => { delete next[id]; });
        return next;
      });
      if (resp.successes < resp.total) {
        const failed = resp.results.filter(r => !r.success);
        console.warn('Bulk stop had failures:', failed);
        alert(`Stopped ${resp.successes}/${resp.total} rooms. ${failed.length} failed.`);
      }
    } catch (e: any) {
      console.error('Bulk stop failed:', e);
      alert(`Bulk stop failed: ${e?.response?.data?.error || e?.message || 'Unknown error'}`);
    } finally {
      setBusyRecordingIds(prev => { const next = new Set(prev); selectedRoomIds.forEach(id => next.delete(id)); return next; });
      setBulkBusy(null);
    }
  }, [selectedRoomIds, bulkBusy]);

  const getRowId = useCallback((params: GetRowIdParams<RoomForGrid>) => String(params.data.id), []);

  const handleRoomDetails = useCallback((id: number) => {
    console.log("Room details clicked for room ID:", id);
    setCurrentRoom(null); // Reset current room before navigating
    setTimeout(() => {
      navigate(`/basic-setup/${id}`);
    }, 100);
  }, [navigate, setCurrentRoom]);

  // Cell Renderer for the Status column. Combines the static "Active" state with a live
  // liveness badge so admins can tell at a glance whether the Electron desktop app is
  // online and whether it has picked up a remote Start command.
  const RoomStatusRenderer: FunctionComponent<ICellRendererParams<RoomForGrid> & {
    remoteStatusById: Record<number, RemoteRecordingStatus>;
    activeRecordingIds: Set<number>;
    clientRecordingIds: Set<number>;
    pendingStartSinceMs: Record<number, number>;
  }> = (props) => {
    const { data, remoteStatusById, activeRecordingIds, clientRecordingIds, pendingStartSinceMs } = props;
    if (!data || !data.id) return null;
    const status = remoteStatusById[data.id];
    const isActive = activeRecordingIds.has(data.id); // admin issued Start
    const isClientRecording = clientRecordingIds.has(data.id); // Electron reports isRecording=true
    const pendingSince = pendingStartSinceMs[data.id];

    // If Electron itself says it's recording we show Recording regardless of who started
    // it -- the speaker may have hit Start on the desktop app directly. We further split
    // this into "audio flowing" (we've received a transcription recently) vs. "no audio
    // yet" (recording but nothing coming back: mic unplugged, muted, permissions denied).
    if (isClientRecording) {
      const audioReceiving = status?.audioReceiving === true;
      if (audioReceiving) {
        return <span className={styles.statusRecording}>● Recording</span>;
      }
      const msAgo = status?.lastTranscriptionMsAgo ?? null;
      const secondsAgoLabel = msAgo != null ? ` (last ${Math.floor(msAgo / 1000)}s ago)` : '';
      return (
        <span
          className={styles.statusWarning}
          title={`The desktop app is recording but no transcription has arrived from the speaker service${msAgo != null ? ' in the last ' + Math.floor(msAgo / 1000) + ' seconds' : ' yet'}. Check the mic on the device.`}
        >
          ⚠ No audio{secondsAgoLabel}
        </span>
      );
    }

    // Nothing happening and admin didn't issue anything -> idle Active badge.
    if (!isActive && !pendingSince) {
      return <span className={styles.statusActive}>Active</span>;
    }

    const elapsedMs = pendingSince ? Date.now() - pendingSince : 0;
    const clientOnline = status?.clientOnline === true;

    // Desktop app offline takes precedence over everything else so admins immediately see
    // that their start command hasn't reached a live speaker.
    if (isActive && status && !clientOnline) {
      return (
        <span className={styles.statusOffline} title="The Manaber speaker app does not appear to be running. Open it on the device assigned to this room.">
          ⚠ Desktop app offline
        </span>
      );
    }

    if (isActive && clientOnline) {
      return (
        <span className={styles.statusWarning} title="Desktop app is online but hasn't confirmed it is recording yet. Check the Manaber speaker app on the device.">
          ⚠ Not recording yet
        </span>
      );
    }

    // First ~10s after Start while we wait for the first poll to arrive from the desktop.
    if (pendingSince && elapsedMs < 10000 && !status) {
      return (
        <span className={styles.statusPending} title="Waiting for the Manaber speaker app to poll…">
          Waiting {Math.floor(elapsedMs / 1000)}s…
        </span>
      );
    }

    return (
      <span className={styles.statusOffline} title="The Manaber speaker app does not appear to be running. Open it on the device assigned to this room.">
        ⚠ Desktop app offline
      </span>
    );
  };

  // Cell Renderer for Actions column
  const RoomActionsRenderer: FunctionComponent<ICellRendererParams<RoomForGrid> & {
    onDetailsClick: (id: number) => void;
    onStartRecordingClick: (id: number) => void;
    onStopRecordingClick: (id: number) => void;
    activeRecordingIds: Set<number>;
    clientRecordingIds: Set<number>;
    busyRecordingIds: Set<number>;
  }> = (props) => {
    const { data, onDetailsClick, onStartRecordingClick, onStopRecordingClick, activeRecordingIds, clientRecordingIds, busyRecordingIds } = props;

    if (!data || !data.id) {
      return null;
    }

    // Treat the room as "recording" if EITHER admin issued a remote-start window OR the
    // Electron app itself reports isRecording=true (started from the desktop directly).
    const isRecording = activeRecordingIds.has(data.id) || clientRecordingIds.has(data.id);
    const isBusy = busyRecordingIds.has(data.id);

    const handleDetailsButtonClick = () => {
      onDetailsClick(data.id);
    };

    const handleOpenSpeakerClick = () => {
      // Generate speaker URL and open in new tab
      const speakerBaseUrl = process.env.REACT_APP_SPEAKER_URL || 'http://localhost:3003';
      const room = rooms.find(r => r.id === data.id);
      if (room && room.roomId) {
        const speakerUrl = `${speakerBaseUrl}/speaker/${room.roomId}${room.speakerPassword ? '?pwd=' + room.speakerPassword : ''}`;
        window.open(speakerUrl, '_blank');
      }
    };

    const handleOpenListenerClick = () => {
      // Generate listener URL and open in new tab
      const listenerBaseUrl = process.env.REACT_APP_PARTICIPANT_URL || 'http://localhost:3000';
      const room = rooms.find(r => r.id === data.id);
      if (room && room.roomId) {
        const listenerUrl = `${listenerBaseUrl}/listener/${room.roomId}${room.listenersPassword ? '?pwd=' + room.listenersPassword : ''}`;
        window.open(listenerUrl, '_blank');
      }
    };

    const handleDeleteRoomClick = async () => {
      const room = rooms.find(r => r.id === data.id);
      const label = room ? `${room.name}${room.roomId ? ` (${room.roomId})` : ''}` : `Room ID ${data.id}`;
      const ok = window.confirm(`Delete ${label}?\n\nThis cannot be undone.`);
      if (!ok) return;

      try {
        setDeletingRoomId(data.id);
        await removeRoom(data.id);
        // Refresh to keep pagination totals accurate
        await fetchRooms({ page, limit: pageSize });
      } catch (e) {
        console.error("Failed to delete room:", e);
        alert("Failed to delete room. Please try again.");
      } finally {
        setDeletingRoomId(null);
      }
    };

    const isDeleting = deletingRoomId === data.id;

    return (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: '8px', position: 'relative', zIndex: 1 }}>
        <button
          className={styles.roomDetailsButton}
          onClick={handleDetailsButtonClick}
          title="View Details"
          style={{ zIndex: 2 }}
        >
          Details
        </button>
        {isRecording ? (
          <button
            className={styles.roomRecordStopButton}
            onClick={() => onStopRecordingClick(data.id)}
            title="Stop recording in the Manaber speaker app"
            style={{ zIndex: 2 }}
            disabled={isBusy}
          >
            {isBusy ? 'Stopping…' : 'Stop Recording'}
          </button>
        ) : (
          <button
            className={styles.roomRecordStartButton}
            onClick={() => onStartRecordingClick(data.id)}
            title="Start recording in the Manaber speaker app"
            style={{ zIndex: 2 }}
            disabled={isBusy}
          >
            {isBusy ? 'Starting…' : 'Start Recording'}
          </button>
        )}
        <button
          className={styles.roomSpeakerButton}
          onClick={handleOpenSpeakerClick}
          title="Open Room as Speaker"
          style={{ zIndex: 2 }}
        >
          Open Room
        </button>
        <button
          className={styles.roomListenerButton}
          onClick={handleOpenListenerClick}
          title="Open Room as Listener"
          style={{ zIndex: 2 }}
        >
          Open Room as Listener
        </button>
        <button
          className={styles.roomDeleteButton}
          onClick={handleDeleteRoomClick}
          title="Delete Room"
          style={{ zIndex: 2 }}
          disabled={isDeleting}
        >
          {isDeleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    );
  };

  const columnDefs = useMemo((): ColDef<RoomForGrid>[] => {
    return [
      { headerName: "Room Id", field: "roomId", valueFormatter: params => params.value || 'N/A', width: 150, sortable: true, filter: false },
      { headerName: "Room Name", field: "name", flex: 1, minWidth: 180, sortable: true, filter: false },
      { headerName: "Room Type", field: "roomType", valueFormatter: params => params.value || 'N/A', width: 150, sortable: true, filter: false },
      {
        headerName: "Status",
        cellRenderer: RoomStatusRenderer,
        cellRendererParams: {
          remoteStatusById,
          activeRecordingIds,
          clientRecordingIds,
          pendingStartSinceMs,
        },
        width: 200,
        sortable: false,
        filter: false,
      },
      {
        headerName: "Actions",
        cellRenderer: RoomActionsRenderer,
        cellRendererParams: {
          onDetailsClick: handleRoomDetails,
          onStartRecordingClick: handleStartRecording,
          onStopRecordingClick: handleStopRecording,
          activeRecordingIds,
          clientRecordingIds,
          busyRecordingIds,
        },
        width: 620, // Widened for five buttons including Start/Stop Recording
        filter: false,
        sortable: false,
        resizable: false,
      },
    ];
  }, [handleRoomDetails, handleStartRecording, handleStopRecording, activeRecordingIds, clientRecordingIds, busyRecordingIds, deletingRoomId, fetchRooms, page, pageSize, removeRoom, rooms, remoteStatusById, pendingStartSinceMs]);

  const onQuickFilterTextChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuickFilterText(event.target.value);
  };

  const handleCreateNewRoom = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentRoom(null);
    setTimeout(() => {
      navigate("/basic-setup");
    }, 100);
  };

  const onFrameClick = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: false, // Disabled filtering by default
    resizable: true,
  }), []);

  const handlePrevPage = () => {
    if (currentPage > 1) setPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setPage(currentPage + 1);
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value, 10);
    setPageSize(newSize);
    setPage(1); // reset to first page when page size changes
  };

  const rangeStart = totalRooms === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = totalRooms === 0 ? 0 : Math.min(currentPage * pageSize, totalRooms);


  return (
    <div className={styles.myRoomScreen}>
      <AdminSideMenu />
      <AdminTopMenu />
      <div className={styles.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingRight: '20px' /* Ensure button doesn't touch edge */ }}>
          <div className={styles.myRooms}>My rooms</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {selectedRoomIds.length > 0 && (
              <>
                <span style={{ color: '#555', fontSize: '0.9rem' }}>
                  {selectedRoomIds.length} selected
                </span>
                <button
                  className={styles.roomRecordStartButton}
                  onClick={handleBulkStart}
                  disabled={bulkBusy !== null}
                  title="Start recording on the speaker app for all selected rooms"
                >
                  {bulkBusy === 'start' ? 'Starting…' : `Start Selected (${selectedRoomIds.length})`}
                </button>
                <button
                  className={styles.roomRecordStopButton}
                  onClick={handleBulkStop}
                  disabled={bulkBusy !== null}
                  title="Stop recording on the speaker app for all selected rooms"
                >
                  {bulkBusy === 'stop' ? 'Stopping…' : `Stop Selected (${selectedRoomIds.length})`}
                </button>
              </>
            )}
            <button
              className={styles.newRoomButton}
              onClick={handleCreateNewRoom}
              title="Create a new room"
            >
              <div className={styles.newRoom}>New room +</div>
            </button>
          </div>
        </div>

        <input
          type="text"
          placeholder="Search"
          value={quickFilterText}
          onChange={onQuickFilterTextChanged}
          className={styles.searchInput}
          style={{ marginBottom: '15px', padding: '10px 15px', width: 'clamp(300px, 40%, 500px)', border: '1px solid #ccc', borderRadius: '5px', fontSize: '1rem', boxSizing: 'border-box' }}
        />

        {loading && (
          <div className={styles.loadingContainer}>
            <div className="loading-spinner"></div> {/* Ensure .loading-spinner CSS is available */}
            <p>Loading rooms...</p>
          </div>
        )}
        {error && (
          <div className={styles.errorContainer}>
            <p>Error: {error}</p>
          </div>
        )}
        {!loading && !error && (
          <div
            className={`ag-theme-alpine ${styles.agGridContainer}`}
            style={{ height: 'calc(100vh - 280px)', width: '100%' }} // Adjusted height, may need further tuning
          >
            <AgGridReact<RoomForGrid>
              rowData={rooms}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              // Use server-side pagination via custom controls below
              pagination={false}
              quickFilterText={quickFilterText}
              overlayNoRowsTemplate={
                '<span style="padding: 10px; border: 1px solid #ddd; background: #f9f9f9; border-radius: 4px; font-style: italic;">No rooms found. Click "New room +" to create one.</span>'
              }
              rowHeight={48}
              rowSelection={{
                mode: 'multiRow',
                checkboxes: true,
                headerCheckbox: true,
                enableClickSelection: false,
              }}
              selectionColumnDef={{
                pinned: 'left',
                width: 50,
                suppressMovable: true,
                resizable: false,
                sortable: false,
              }}
              onSelectionChanged={onSelectionChanged}
              getRowId={getRowId}
            />
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && !error && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
            <div style={{ color: '#555' }}>
              Showing {rangeStart}-{rangeEnd} of {totalRooms}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={handlePrevPage}
                disabled={currentPage <= 1}
                className={styles.paginationButton}
                title="Previous page"
              >
                Prev
              </button>
              <span style={{ minWidth: 90, textAlign: 'center' }}>Page {currentPage} of {totalPages}</span>
              <button
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
                className={styles.paginationButton}
                title="Next page"
              >
                Next
              </button>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#555' }}>Rows per page:</span>
                <select value={pageSize} onChange={handlePageSizeChange} className={styles.pageSizeSelect}>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyRoomScreen;
